import React, { useState, useContext } from 'react';
import { AuthContext } from '../../components/AuthWrapper';
import { Navigate, Link } from 'react-router-dom';

const UserManagement = () => {
  const { isAdmin } = useContext(AuthContext);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'user',
    accessToken: ''
  });
  
  // Example users - in a real implementation, these would come from Firestore
  const [users, setUsers] = useState([
    {
      id: 'user1',
      username: 'Attorney Smith',
      email: 'smith@law.example.com',
      role: 'user',
      accessToken: 'atkinson2024'
    },
    {
      id: 'admin1',
      username: 'Landon Mayo',
      email: 'landonmayo722@gmail.com',
      role: 'admin',
      accessToken: 'admin2024'
    }
  ]);

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddUser = () => {
    setFormData({
      id: `user-${Date.now()}`,
      username: '',
      email: '',
      role: 'user',
      accessToken: generateToken(8)
    });
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    setFormData({...user});
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (users.some(user => user.id === formData.id)) {
      // Update existing user
      setUsers(users.map(user => user.id === formData.id ? formData : user));
    } else {
      // Add new user
      setUsers([...users, formData]);
    }
    
    setShowForm(false);
    setFormData({
      username: '',
      email: '',
      role: 'user',
      accessToken: ''
    });
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(user => user.id !== userId));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      username: '',
      email: '',
      role: 'user',
      accessToken: ''
    });
  };

  const generateToken = (length = 8) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      result += charset[randomIndex];
    }
    
    return result;
  };

  const handleRegenerateToken = () => {
    setFormData({
      ...formData,
      accessToken: generateToken(8)
    });
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-navy">User Management</h1>
        <div className="space-x-4">
          <button 
            onClick={handleAddUser}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          >
            Add New User
          </button>
          <Link 
            to="/admin"
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-8">
        {/* User Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">
              {formData.id ? 'Edit User' : 'Add New User'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="user">Regular User</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                <div className="flex">
                  <input
                    type="text"
                    name="accessToken"
                    value={formData.accessToken}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-l"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleRegenerateToken}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-r"
                  >
                    Generate
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">This is the token the user will use to log in.</p>
              </div>
              
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  {formData.id ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Users List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Token</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4">{user.username}</td>
                  <td className="px-6 py-4">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      user.role === 'admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role === 'admin' ? 'Administrator' : 'Regular User'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                      {user.accessToken}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;